// A live preview of a theme on real-looking screens: a list, a thread, a card and the tab bar.
// The draft's variables sit on this wrapper alone (not on <html>), so the rest of the app does
// not change while a theme is being made.
import { useMemo } from 'react'
import { Check, CalendarDays, Home as HomeIcon, Inbox, Sparkles } from 'lucide-react'
import { themeSpecToVars, type StudioMode, type ThemeSpec } from '@/lib/themeStudio'
import { StudioBackground } from '@/components/StudioBackground'
import { cx } from '@/components/ui'

const ROWS = [
  { t: 'Call the dentist', m: 'Today', done: false },
  { t: 'Plan the weekend', m: 'Tomorrow', done: false },
  { t: 'Send the invoice', m: 'Done', done: true },
]

export const ThemePreview = ({
  spec,
  mode,
  compact,
}: {
  spec: ThemeSpec
  mode: StudioMode
  compact?: boolean
}) => {
  const draw = useMemo(() => themeSpecToVars(spec, mode), [spec, mode])
  const style = Object.fromEntries(
    Object.entries(draw.vars).filter(([k]) => k !== 'color-scheme'),
  ) as React.CSSProperties
  return (
    <div
      className="relative isolate overflow-hidden rounded-[var(--radius-card)] border border-line text-fg"
      style={{ ...style, background: 'var(--color-ink)', colorScheme: mode === 'light' ? 'light' : 'dark' }}
      data-testid="theme-preview"
      data-mode={mode}
    >
      <StudioBackground draw={draw.background} contained />
      <div className="relative flex flex-col gap-3 p-3">
        <div className="px-1 text-[22px] font-semibold leading-tight">Today</div>
        <div className="group-list" style={{ background: 'var(--color-panel)' }}>
          {ROWS.map((r) => (
            <div key={r.t} className="flex items-center gap-2.5 px-3 py-2.5 border-b border-line last:border-0">
              <span
                className="h-5 w-5 rounded-full border flex items-center justify-center shrink-0"
                style={{
                  borderColor: r.done ? 'var(--color-accent)' : 'var(--color-line-strong)',
                  background: r.done ? 'var(--color-accent)' : 'transparent',
                }}
              >
                {r.done && <Check size={12} style={{ color: 'var(--color-on-accent)' }} />}
              </span>
              <span className={cx('flex-1 text-[15px]', r.done && 'line-through opacity-60')}>{r.t}</span>
              <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                {r.m}
              </span>
            </div>
          ))}
        </div>
        {!compact && (
          <>
            <div className="flex flex-col gap-1.5">
              <div
                className="self-start max-w-[80%] rounded-[var(--radius-bubble)] px-3 py-2 text-sm"
                style={{ background: 'var(--color-raised)' }}
              >
                Your day is light. Want me to move the invoice up?
              </div>
              <div
                className="self-end max-w-[80%] rounded-[var(--radius-bubble)] px-3 py-2 text-sm"
                style={{ background: 'var(--color-accent)', color: 'var(--color-on-accent)' }}
              >
                Yes please
              </div>
            </div>
            <div className="card p-3" style={{ background: 'var(--color-panel)' }}>
              <div className="font-semibold">Sunday plan</div>
              <div className="text-sm mt-0.5" style={{ color: 'var(--color-muted)' }}>
                Three things, nothing urgent. <span style={{ color: 'var(--color-faint)' }}>Updated now</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2.5 text-xs">
                {(['ok', 'warn', 'danger', 'info'] as const).map((k) => (
                  <span
                    key={k}
                    className="rounded-md px-1.5 h-5 inline-flex items-center border"
                    style={{ color: `var(--color-${k})`, borderColor: `var(--color-${k})` }}
                  >
                    {k}
                  </span>
                ))}
                <span
                  className="rounded-md px-1.5 h-5 inline-flex items-center"
                  style={{ color: 'var(--color-highlight, var(--color-accent))' }}
                >
                  highlight
                </span>
              </div>
              <button
                type="button"
                tabIndex={-1}
                className="mt-3 min-h-9 px-3 text-sm font-semibold rounded-[var(--radius-control)]"
                style={{ background: 'var(--color-accent)', color: 'var(--color-on-accent)' }}
              >
                Start
              </button>
            </div>
          </>
        )}
        <div
          className="flex justify-around rounded-full border border-line py-2"
          style={{ background: 'var(--color-panel)' }}
          aria-hidden
        >
          {[HomeIcon, Inbox, CalendarDays, Sparkles].map((I, i) => (
            <span
              key={i}
              className="h-8 w-12 rounded-full flex items-center justify-center"
              style={
                i === 0
                  ? { background: 'var(--color-accent-soft)', color: 'var(--color-accent-text, var(--color-accent))' }
                  : { color: 'var(--color-muted)' }
              }
            >
              <I size={18} />
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
